import React, { useState } from 'react';
import LanguageSwitcher from './ui/LanguageSwitcher';
import { LanguageOption } from '../types';
import i18n from '../i18n/config';
import { Menu, X, ChevronDown, Home, Car, Briefcase, Wallet } from 'lucide-react';

interface HeaderProps {
  currentLanguage: string;
}

const Header: React.FC<HeaderProps> = ({ currentLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  
  const languageOptions: LanguageOption[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'lv', label: 'Latviešu', flag: '🇱🇻' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  ];

  const loanTypes = [
    {
      id: 'consumer',
      title: i18n.t('loan_types_page.consumer.title'),
      icon: Wallet,
      anchor: i18n.t('anchor_links.consumer')
    },
    {
      id: 'mortgage',
      title: i18n.t('loan_types_page.mortgage.title'),
      icon: Home,
      anchor: i18n.t('anchor_links.mortage')
    },
    {
      id: 'business',
      title: i18n.t('loan_types_page.business.title'),
      icon: Briefcase,
      anchor: i18n.t('anchor_links.business')
    },
    {
      id: 'car',
      title: i18n.t('loan_types_page.car.title'),
      icon: Car,
      anchor: i18n.t('anchor_links.car')
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'unset';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    document.body.style.overflow = 'unset';
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const closeServices = () => {
    setIsServicesOpen(false);
  };

  const Logo = () => (
    <a href="/" className="flex items-center">
      <div className="flex-shrink-0">
        <div className="h-10 w-10 rounded-full bg-[#1abc9c] flex items-center justify-center text-white font-bold text-xl shadow-md">
          {i18n.t('meta.og_title').charAt(0)}
        </div>
      </div>
      <div className="ml-3">
        <h1 className="text-xl font-bold text-[#2c3e50]">{i18n.t('header.logo.title')}</h1>
        <p className="text-md text-[#1abc9c]">{i18n.t('header.logo.description')}</p>
      </div>
    </a>
  );
  
  return (
    <header className="bg-white shadow-sm relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Logo />
          
          {/* Desktop Navigation */}
          <nav className="hidden sm:flex items-center space-x-6">
            {/* Services Dropdown */}
            <div className="relative">
              <button
                onClick={toggleServices}
                onBlur={(e) => {
                  // Only close if the blur is not to a child element
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setTimeout(closeServices, 150);
                  }
                }}
                className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200 focus:outline-none"
              >
                <span>{i18n.t('footer.quick_links.services')}</span>
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isServicesOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {loanTypes.map((loanType) => {
                    const IconComponent = loanType.icon;
                    return (
                      <a
                        key={loanType.id}
                        href={`/${i18n.t('routes.loan_types')}#${loanType.anchor}`}
                        className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors duration-200"
                        onClick={closeServices}
                      >
                        <IconComponent 
                          className="h-5 w-5 mr-3 text-gray-500" 
                        />
                        <span className="text-sm font-medium">{loanType.title}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            <a 
              href={`#${i18n.t('anchor_links.about_us')}`}
              className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
            >
              {i18n.t('about.title')}
            </a>
            <a 
              href={`/${i18n.t('routes.contact')}`}
              className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
            >
              {i18n.t('contact.title')}
            </a>
            <a 
              href={`#${i18n.t('anchor_links.faq')}`}
              className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
            >
              {i18n.t('footer.quick_links.faq')}
            </a>
            
            <LanguageSwitcher
              options={languageOptions}
              currentLanguage={currentLanguage}
            />
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="sm:hidden p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 transform transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        } sm:hidden`}
      >
        {/* Fullscreen Menu */}
        <div className="absolute inset-0 bg-white">
          <div className="flex flex-col h-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="flex justify-between items-center py-4">
                <Logo />
                <button
                  className="p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto">
              {/* Services Dropdown */}
              <div className="space-y-2">
                <button
                  onClick={toggleServices}
                  className="flex text-left w-full px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  <ChevronDown className={`h-5 w-5 mr-2 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                  <span>{i18n.t('footer.quick_links.services')}</span>
                </button>
                
                {isServicesOpen && (
                  <div className="ml-4 space-y-1">
                    {loanTypes.map((loanType) => {
                      const IconComponent = loanType.icon;
                      return (
                        <a
                          key={loanType.id}
                          href={`/${i18n.t('routes.loan_types')}#${loanType.anchor}`}
                          className="flex items-center px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                          onClick={closeMobileMenu}
                        >
                          <IconComponent 
                            className="h-5 w-5 mr-3 text-gray-500" 
                          />
                          <span className="font-medium">{loanType.title}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-2">
                <a
                  href={`#${i18n.t('anchor_links.about_us')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('about.title')}
                </a>
                <a
                  href={`/${i18n.t('routes.contact')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('contact.title')}
                </a>
                <a
                  href={`#${i18n.t('anchor_links.faq')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('footer.quick_links.faq')}
                </a>
              </div>
            </nav>

            <div className="p-6 border-t border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">{i18n.t('languages.select_language')}</h3>
                <LanguageSwitcher
                  options={languageOptions}
                  currentLanguage={currentLanguage}
                />
              </div>
              <p className="text-sm text-gray-500">
                {i18n.t('footer.copyright', { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;